/**
 * Schedule row: which calendar hosts the interview (shared calendars).
 * MVP: summary + modal picker; uses the same polling hook as Scheduling Preferences.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Flex,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Text,
} from '@chakra-ui/react';
import { useMailBox } from '@/hooks/use-mailbox';
import { useSharedCalendarPolling } from '@/pages/settings/SchedulingPreferences/SharedCalendar/useSharedCalendarPolling';
import { OauthModal } from '@/components/ats/oauth';
import { CalendarField } from './CalendarField';
import type { ApiReference } from '../../../config/schedule.types';

export type CalendarInfoFormValue = {
  provider_calendar_id: string;
  is_primary?: boolean;
} | null;

interface SharedCal {
  provider_calendar_id?: string;
  name?: string;
  is_primary?: boolean;
  is_default?: boolean;
  status?: string;
}

interface ScheduleCalendarRowFieldProps {
  interviewPlan?: Record<string, unknown>;
  apiReference?: ApiReference;
  field: { value: unknown };
  formMethods: {
    watch: (_n: string) => unknown;
    setValue: (
      _n: string,
      _v: unknown,
      _opts?: { shouldDirty?: boolean }
    ) => void;
  };
}

function parseCalendarInfo(value: unknown): CalendarInfoFormValue {
  if (!value || typeof value !== 'object') return null;
  const o = value as Record<string, unknown>;
  const id = o.provider_calendar_id;
  if (typeof id !== 'string' || !id) return null;
  return {
    provider_calendar_id: id,
    is_primary: Boolean(o.is_primary),
  };
}

export const ScheduleCalendarRowField = ({
  interviewPlan,
  apiReference,
  field,
  formMethods,
}: ScheduleCalendarRowFieldProps) => {
  const { isCalConnected } = useMailBox();
  const [modalOpen, setModalOpen] = useState(false);
  const [oauthOpen, setOauthOpen] = useState(false);
  const seededDefaultRef = useRef(false);

  const isEdit = apiReference === 'edit';
  const sharedEnabled = Boolean(interviewPlan?.shared_calendar_enabled);

  const { isLoading, sharedCalendarOptions, refetchUuid } =
    useSharedCalendarPolling({
      type: 'saved',
      autoFetch: sharedEnabled && isCalConnected,
    });

  const calendars: SharedCal[] = useMemo(
    () =>
      (sharedCalendarOptions?.calendars as SharedCal[] | undefined)?.filter(
        (c) => c?.status !== 'deleted_from_provider'
      ) ?? [],
    [sharedCalendarOptions]
  );

  const calendarInfo = parseCalendarInfo(field.value);

  const scheduledCal = interviewPlan?.scheduled_calendar as
    | {
        calendar_name?: string;
        provider_calendar_id?: string;
        is_primary?: boolean;
      }
    | undefined;

  const resolveSelectedCal = useCallback(
    (id: string | undefined) =>
      id ? calendars.find((c) => c.provider_calendar_id === id) : undefined,
    [calendars]
  );

  // Seed form with default calendar once options load (create / no prior selection).
  useEffect(() => {
    if (!sharedEnabled || !isCalConnected || calendars.length === 0) return;
    if (seededDefaultRef.current) return;
    if (isEdit) return;
    if (calendarInfo?.provider_calendar_id) return;
    const fromPlan = interviewPlan?.calendar_info as
      | { provider_calendar_id?: string }
      | undefined;
    if (fromPlan?.provider_calendar_id) return;

    const def = calendars.find((c) => c.is_default || c.is_primary);
    if (!def?.provider_calendar_id) return;

    seededDefaultRef.current = true;
    formMethods.setValue(
      'calendar_info',
      {
        provider_calendar_id: def.provider_calendar_id,
        is_primary: Boolean(def.is_primary),
      },
      { shouldDirty: false }
    );
  }, [
    calendarInfo?.provider_calendar_id,
    calendars,
    formMethods,
    interviewPlan?.calendar_info,
    isCalConnected,
    isEdit,
    sharedEnabled,
  ]);

  const summaryName = useMemo(() => {
    if (isEdit && scheduledCal?.calendar_name) {
      return scheduledCal.calendar_name;
    }
    const id = calendarInfo?.provider_calendar_id;
    const cal = resolveSelectedCal(id);
    return cal?.name ?? 'Organizer calendar';
  }, [
    calendarInfo?.provider_calendar_id,
    isEdit,
    resolveSelectedCal,
    scheduledCal?.calendar_name,
  ]);

  const showYouSuffix = useMemo(() => {
    if (isEdit) {
      return Boolean(scheduledCal?.is_primary);
    }
    const id = calendarInfo?.provider_calendar_id;
    const cal = resolveSelectedCal(id);
    return Boolean(cal?.is_primary);
  }, [
    calendarInfo?.provider_calendar_id,
    isEdit,
    resolveSelectedCal,
    scheduledCal?.is_primary,
  ]);

  const openPicker = useCallback(() => {
    if (!isCalConnected) {
      setOauthOpen(true);
      return;
    }
    void refetchUuid();
    setModalOpen(true);
  }, [isCalConnected, refetchUuid]);

  const pickCalendar = useCallback(
    (cal: SharedCal) => {
      if (!cal.provider_calendar_id) return;
      formMethods.setValue('calendar_info', {
        provider_calendar_id: cal.provider_calendar_id,
        is_primary: Boolean(cal.is_primary),
      });
      setModalOpen(false);
    },
    [formMethods]
  );

  if (!interviewPlan) {
    return null;
  }

  const canEdit = Boolean(interviewPlan.shared_calendar_enabled);

  return (
    <Box w="100%" pb="1">
      <CalendarField
        calendarName={`${summaryName}${showYouSuffix ? ' (You)' : ''}`}
        onEdit={canEdit ? openPicker : undefined}
        disabled={!canEdit}
      />

      <OauthModal
        isOpen={oauthOpen}
        onClose={() => setOauthOpen(false)}
        headerText="Connect your calendar"
        subHeaderText="Add new shared calendars or modify existing ones by connecting your calendar with Kula"
      />

      <Modal isOpen={modalOpen} onClose={() => setModalOpen(false)} size="md">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader fontSize="md">Choose calendar</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb="6">
            {isLoading ? (
              <Flex py="8" justify="center">
                <Spinner />
              </Flex>
            ) : calendars.length === 0 ? (
              <Text fontSize="sm" color="secondary.600">
                No shared calendars available. Connect your calendar or add
                shared calendars in Scheduling preferences.
              </Text>
            ) : (
              <Flex direction="column" gap="1">
                {calendars.map((cal) => {
                  const id = cal.provider_calendar_id;
                  if (!id) return null;
                  const selected =
                    calendarInfo?.provider_calendar_id === id ||
                    (!calendarInfo &&
                      scheduledCal?.provider_calendar_id === id);
                  return (
                    <Flex
                      key={id}
                      as="button"
                      type="button"
                      w="100%"
                      px="3"
                      py="2"
                      borderRadius="md"
                      borderWidth="1px"
                      borderColor={selected ? 'secondary.400' : 'border-color'}
                      bg={selected ? 'secondary.25' : 'transparent'}
                      cursor="pointer"
                      align="center"
                      justify="space-between"
                      onClick={() => pickCalendar(cal)}
                    >
                      <Text fontSize="sm" fontWeight="medium" textAlign="left">
                        {cal.name ?? id}
                        {cal.is_primary ? ' (You)' : ''}
                      </Text>
                    </Flex>
                  );
                })}
              </Flex>
            )}
          </ModalBody>
        </ModalContent>
      </Modal>
    </Box>
  );
};
